/**
 * Score-based filter sections for the mobile filter sheet.
 *
 * Renders three collapsible sections: FIT & RATINGS, PERFORMANCE,
 * and GOALKEEPER — each containing RangeFilterField components
 * for 0-100 score ranges.
 * @module features/scouting/components/MobileFilterSheet/ScoreFilterSections/ScoreFilterSectionsView
 */

import { ChevronDown, ChevronUp } from "lucide-react-native";
import React, { memo } from "react";

import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

import ActiveFilterCountBadge from "@/components/organisms/shared/ActiveFilterCountBadge";

import CustomMetricFilterFields from "@/features/scouting/components/organisms/CustomMetricFilterFields";
import RangeFilterField from "@/components/organisms/shared/RangeFilterField";

import {
  DEFAULT_CUSTOM_STAT_RANGE,
  DEFAULT_SCORE_RANGE,
  isRangeActive,
} from "@/constants/filterDefaults";

/** Props for ScoreFilterSectionsView */
interface ScoreFilterSectionsViewProps {
  /** Whether FIT & RATINGS section is expanded */
  readonly isFitRatingsOpen: boolean;
  /** Toggle FIT & RATINGS section */
  readonly onToggleFitRatings: () => void;
  /** GPR range [min, max] */
  readonly gprRange: [number, number];
  /** GPR range change handler */
  readonly onSetGprRange: (range: [number, number]) => void;
  /** Player Style Fit range [min, max] */
  readonly fitScoreRange: [number, number];
  /** Fit score range change handler */
  readonly onSetFitScoreRange: (range: [number, number]) => void;
  /** Team Style Fit range [min, max] */
  readonly teamStyleFitRange: [number, number];
  /** Team style fit range change handler */
  readonly onSetTeamStyleFitRange: (range: [number, number]) => void;
  /** Whether PERFORMANCE section is expanded */
  readonly isPerformanceOpen: boolean;
  /** Toggle PERFORMANCE section */
  readonly onTogglePerformance: () => void;
  /** Carrying range [min, max] */
  readonly carryingRange: [number, number];
  /** Carrying range change handler */
  readonly onSetCarryingRange: (range: [number, number]) => void;
  /** Dribbling range [min, max] */
  readonly dribblingRange: [number, number];
  /** Dribbling range change handler */
  readonly onSetDribblingRange: (range: [number, number]) => void;
  /** Passing range [min, max] */
  readonly passingRange: [number, number];
  /** Passing range change handler */
  readonly onSetPassingRange: (range: [number, number]) => void;
  /** Shooting range [min, max] */
  readonly shootingRange: [number, number];
  /** Shooting range change handler */
  readonly onSetShootingRange: (range: [number, number]) => void;
  /** Whether GOALKEEPER section is expanded */
  readonly isGoalkeeperOpen: boolean;
  /** Toggle GOALKEEPER section */
  readonly onToggleGoalkeeper: () => void;
  /** GK Passing range [min, max] */
  readonly gkPassingRange: [number, number];
  /** GK Passing range change handler */
  readonly onSetGkPassingRange: (range: [number, number]) => void;
  /** GK Shot Stopping range [min, max] */
  readonly gkShotStoppingRange: [number, number];
  /** GK Shot Stopping range change handler */
  readonly onSetGkShotStoppingRange: (range: [number, number]) => void;
  /** GK Positioning range [min, max] */
  readonly gkPositioningRange: [number, number];
  /** GK Positioning range change handler */
  readonly onSetGkPositioningRange: (range: [number, number]) => void;
  /** Whether REPORT STATS section is expanded */
  readonly isReportStatsOpen: boolean;
  /** Toggle REPORT STATS section */
  readonly onToggleReportStats: () => void;
  /** Available org-level custom metrics for filters */
  readonly customMetrics: readonly {
    readonly id: string;
    readonly displayName: string;
  }[];
  /** Current custom stat range values */
  readonly customStatRanges: Readonly<
    Record<string, readonly [number, number]>
  >;
  /** Custom stat range change handler */
  readonly onSetCustomStatRange: (
    name: string,
    range: [number, number]
  ) => void;
  /** Per-section active filter counts for badge display */
  readonly fitRatingsFilterCount: number;
  readonly performanceFilterCount: number;
  readonly goalkeeperFilterCount: number;
  readonly reportStatsFilterCount: number;
  /** Whether dark mode is active — for portal-safe styling */
  readonly isDark?: boolean;
}

/** Section header style constant */
const SECTION_HEADER_CLASS = "flex-row items-center justify-between py-3";

/** Section border className for theme-aware borders */
const SECTION_BORDER_CLASS =
  "border-b border-outline-default dark:border-needs-semantic-outline-800";

/**
 * Renders FIT & RATINGS, PERFORMANCE, and GOALKEEPER filter sections
 * @param root0 - Score filter props
 * @param root0.isFitRatingsOpen - FIT & RATINGS expanded
 * @param root0.onToggleFitRatings - Toggle FIT & RATINGS
 * @param root0.gprRange - GPR range
 * @param root0.onSetGprRange - GPR handler
 * @param root0.fitScoreRange - Fit score range
 * @param root0.onSetFitScoreRange - Fit score handler
 * @param root0.teamStyleFitRange - Team style fit range
 * @param root0.onSetTeamStyleFitRange - Team style fit handler
 * @param root0.isPerformanceOpen - PERFORMANCE expanded
 * @param root0.onTogglePerformance - Toggle PERFORMANCE
 * @param root0.carryingRange - Carrying range
 * @param root0.onSetCarryingRange - Carrying handler
 * @param root0.dribblingRange - Dribbling range
 * @param root0.onSetDribblingRange - Dribbling handler
 * @param root0.passingRange - Passing range
 * @param root0.onSetPassingRange - Passing handler
 * @param root0.shootingRange - Shooting range
 * @param root0.onSetShootingRange - Shooting handler
 * @param root0.isGoalkeeperOpen - GOALKEEPER expanded
 * @param root0.onToggleGoalkeeper - Toggle GOALKEEPER
 * @param root0.gkPassingRange - GK Passing range
 * @param root0.onSetGkPassingRange - GK Passing handler
 * @param root0.gkShotStoppingRange - GK Shot Stopping range
 * @param root0.onSetGkShotStoppingRange - GK Shot Stopping handler
 * @param root0.gkPositioningRange - GK Positioning range
 * @param root0.onSetGkPositioningRange - GK Positioning handler
 * @param root0.isReportStatsOpen - REPORT STATS expanded
 * @param root0.onToggleReportStats - Toggle REPORT STATS
 * @param root0.customMetrics - Available custom metrics
 * @param root0.customStatRanges - Custom stat range values
 * @param root0.onSetCustomStatRange - Custom stat range handler
 * @param root0.fitRatingsFilterCount - Active filter count for Fit & Ratings
 * @param root0.performanceFilterCount - Active filter count for Performance
 * @param root0.goalkeeperFilterCount - Active filter count for Goalkeeper
 * @param root0.reportStatsFilterCount - Active filter count for Report Stats
 * @param root0.isDark - Whether dark mode is active
 */
const ScoreFilterSectionsView = ({
  isFitRatingsOpen,
  onToggleFitRatings,
  gprRange,
  onSetGprRange,
  fitScoreRange,
  onSetFitScoreRange,
  teamStyleFitRange,
  onSetTeamStyleFitRange,
  isPerformanceOpen,
  onTogglePerformance,
  carryingRange,
  onSetCarryingRange,
  dribblingRange,
  onSetDribblingRange,
  passingRange,
  onSetPassingRange,
  shootingRange,
  onSetShootingRange,
  isGoalkeeperOpen,
  onToggleGoalkeeper,
  gkPassingRange,
  onSetGkPassingRange,
  gkShotStoppingRange,
  onSetGkShotStoppingRange,
  gkPositioningRange,
  onSetGkPositioningRange,
  isReportStatsOpen,
  onToggleReportStats,
  customMetrics,
  customStatRanges,
  onSetCustomStatRange,
  fitRatingsFilterCount,
  performanceFilterCount,
  goalkeeperFilterCount,
  reportStatsFilterCount,
  isDark,
}: ScoreFilterSectionsViewProps) => (
  <>
    {/* FIT & RATINGS */}
    <Stack className={SECTION_BORDER_CLASS}>
      <Pressable onPress={onToggleFitRatings} className={SECTION_HEADER_CLASS}>
        <Stack direction="horizontal" className="items-center gap-2">
          <Text variant="title-sm" className="uppercase text-content-primary">
            FIT & RATINGS
          </Text>
          <ActiveFilterCountBadge count={fitRatingsFilterCount} />
        </Stack>
        <Icon
          as={isFitRatingsOpen ? ChevronUp : ChevronDown}
          size="sm"
          className="text-content-subtle"
        />
      </Pressable>
      {isFitRatingsOpen ? (
        <Stack className="gap-4 pb-4">
          <RangeFilterField
            key="gpr"
            label="GPR"
            value={gprRange}
            minValue={DEFAULT_SCORE_RANGE[0]}
            maxValue={DEFAULT_SCORE_RANGE[1]}
            step={1}
            onChange={onSetGprRange}
            isDark={isDark}
          />
          <RangeFilterField
            key="fit"
            label="Player Style Fit"
            value={fitScoreRange}
            minValue={DEFAULT_SCORE_RANGE[0]}
            maxValue={DEFAULT_SCORE_RANGE[1]}
            step={1}
            onChange={onSetFitScoreRange}
            isDark={isDark}
          />
          <RangeFilterField
            key="teamfit"
            label="Team Style Fit"
            value={teamStyleFitRange}
            minValue={DEFAULT_SCORE_RANGE[0]}
            maxValue={DEFAULT_SCORE_RANGE[1]}
            step={1}
            onChange={onSetTeamStyleFitRange}
            isDark={isDark}
          />
        </Stack>
      ) : null}
    </Stack>

    {/* PERFORMANCE */}
    <Stack className={SECTION_BORDER_CLASS}>
      <Pressable onPress={onTogglePerformance} className={SECTION_HEADER_CLASS}>
        <Stack direction="horizontal" className="items-center gap-2">
          <Text variant="title-sm" className="uppercase text-content-primary">
            PERFORMANCE
          </Text>
          <ActiveFilterCountBadge count={performanceFilterCount} />
        </Stack>
        <Icon
          as={isPerformanceOpen ? ChevronUp : ChevronDown}
          size="sm"
          className="text-content-subtle"
        />
      </Pressable>
      {isPerformanceOpen ? (
        <Stack className="gap-4 pb-4">
          <RangeFilterField
            key="carrying"
            label="Carrying"
            value={carryingRange}
            minValue={DEFAULT_SCORE_RANGE[0]}
            maxValue={DEFAULT_SCORE_RANGE[1]}
            step={1}
            onChange={onSetCarryingRange}
            isDark={isDark}
          />
          <RangeFilterField
            key="dribbling"
            label="Dribbling"
            value={dribblingRange}
            minValue={DEFAULT_SCORE_RANGE[0]}
            maxValue={DEFAULT_SCORE_RANGE[1]}
            step={1}
            onChange={onSetDribblingRange}
            isDark={isDark}
          />
          <RangeFilterField
            key="passing"
            label="Passing"
            value={passingRange}
            minValue={DEFAULT_SCORE_RANGE[0]}
            maxValue={DEFAULT_SCORE_RANGE[1]}
            step={1}
            onChange={onSetPassingRange}
            isDark={isDark}
          />
          <RangeFilterField
            key="shooting"
            label="Shooting"
            value={shootingRange}
            minValue={DEFAULT_SCORE_RANGE[0]}
            maxValue={DEFAULT_SCORE_RANGE[1]}
            step={1}
            onChange={onSetShootingRange}
            isDark={isDark}
          />
        </Stack>
      ) : null}
    </Stack>

    {/* GOALKEEPER */}
    <Stack className={SECTION_BORDER_CLASS}>
      <Pressable onPress={onToggleGoalkeeper} className={SECTION_HEADER_CLASS}>
        <Stack direction="horizontal" className="items-center gap-2">
          <Text variant="title-sm" className="uppercase text-content-primary">
            GOALKEEPER
          </Text>
          <ActiveFilterCountBadge count={goalkeeperFilterCount} />
        </Stack>
        <Icon
          as={isGoalkeeperOpen ? ChevronUp : ChevronDown}
          size="sm"
          className="text-content-subtle"
        />
      </Pressable>
      {isGoalkeeperOpen ? (
        <Stack className="gap-4 pb-4">
          <RangeFilterField
            key="gkpass"
            label="GK Passing"
            value={gkPassingRange}
            minValue={DEFAULT_SCORE_RANGE[0]}
            maxValue={DEFAULT_SCORE_RANGE[1]}
            step={1}
            onChange={onSetGkPassingRange}
            isDark={isDark}
          />
          <RangeFilterField
            key="gkstop"
            label="GK Shot Stopping"
            value={gkShotStoppingRange}
            minValue={DEFAULT_SCORE_RANGE[0]}
            maxValue={DEFAULT_SCORE_RANGE[1]}
            step={1}
            onChange={onSetGkShotStoppingRange}
            isDark={isDark}
          />
          <RangeFilterField
            key="gkpos"
            label="GK Positioning"
            value={gkPositioningRange}
            minValue={DEFAULT_SCORE_RANGE[0]}
            maxValue={DEFAULT_SCORE_RANGE[1]}
            step={1}
            onChange={onSetGkPositioningRange}
            isDark={isDark}
          />
        </Stack>
      ) : null}
    </Stack>

    {/* REPORT STATS */}
    {customMetrics.length > 0 ||
    Object.values(customStatRanges).some(range =>
      isRangeActive(range as [number, number], DEFAULT_CUSTOM_STAT_RANGE)
    ) ? (
      <Stack className={SECTION_BORDER_CLASS}>
        <Pressable
          onPress={onToggleReportStats}
          className={SECTION_HEADER_CLASS}
        >
          <Stack direction="horizontal" className="items-center gap-2">
            <Text variant="title-sm" className="uppercase text-content-primary">
              REPORT STATS
            </Text>
            <ActiveFilterCountBadge count={reportStatsFilterCount} />
          </Stack>
          <Icon
            as={isReportStatsOpen ? ChevronUp : ChevronDown}
            size="sm"
            className="text-content-subtle"
          />
        </Pressable>
        {isReportStatsOpen ? (
          <Stack className="gap-4 pb-4">
            <CustomMetricFilterFields
              customMetrics={customMetrics}
              customStatRanges={customStatRanges}
              onSetCustomStatRange={onSetCustomStatRange}
              isDark={isDark}
            />
          </Stack>
        ) : null}
      </Stack>
    ) : null}
  </>
);

ScoreFilterSectionsView.displayName = "ScoreFilterSectionsView";

export default memo(ScoreFilterSectionsView);
